import React, { useEffect, useState, useCallback } from "react";

function Demande({ clientId }) {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [loadingForm, setLoadingForm] = useState(false);

  const [formData, setFormData] = useState({
    type: "",
    urgence: "Normal",
    details: "",
  });

  // 🔄 Récupération des demandes

  const fetchDemandes = useCallback(() => {
    if (!clientId) return;

    setLoading(true);
    fetch(`https://backend-myalfa.vercel.app/api/demande/client/${clientId}`)
      .then((res) => res.json())
      .then((data) => {
        setDemandes(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [clientId]);

  useEffect(() => {
    fetchDemandes();
  }, [fetchDemandes]);

  // ➕ Ajouter une demande
  const handleSubmit = (e) => {
    setLoadingForm(true);
    e.preventDefault();

    fetch("https://backend-myalfa.vercel.app/api/demande", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...formData,
        etat: "En cours",
        clientId,
      }),
    })
      .then((res) => res.json())
      .then(() => {
        fetchDemandes(); // 🔄 recharger depuis l’API
        setShowForm(false);
        setFormData({ type: "rib", urgence: "Normal", details: "" });
        setActiveTab("all");
      });

    setLoadingForm(false);
  };

  // 📌 Filtres
  const enCours = demandes.filter((d) => d.etat === "En cours");
  const terminees = demandes.filter((d) => d.etat === "Terminés");

  const getDemandesByTab = () => {
    if (activeTab === "en_cours") return enCours;
    if (activeTab === "terminees") return terminees;
    return demandes;
  };

  const renderTypeLabel = (type) => {
    switch (type) {
      case "kbis":
        return "Extrait K-bis";
      case "attestation_vigilance":
        return "Attestation de vigilance";
      case "attestation_fiscale":
        return "Attestation fiscale";
      case "rib":
        return "RIB";
      case "situation_comptable":
        return "Situation comptable";
      case "autre":
        return "Autre demande";
      default:
        return type;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold">
          Demandez vos documents administratifs
        </h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-[#8B1538] text-white rounded"
        >
          Ajouter une Demande
        </button>
      </div>

      {/* Formulaire */}
      {showForm && (
        <form onSubmit={handleSubmit} className="p-4 border rounded space-y-6">
          {/* Type en grid 3x2 */}
          <div>
            <label className="block text-sm font-medium mb-2">Type</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: "kbis", label: "Extrait K-bis" },
                {
                  value: "attestation_vigilance",
                  label: "Attestation de vigilance",
                },
                { value: "attestation_fiscale", label: "Attestation fiscale" },
                { value: "rib", label: "RIB" },
                { value: "situation_comptable", label: "Situation comptable" },
                { value: "autre", label: "Autre demande" },
              ].map((option) => (
                <button
                  type="button"
                  key={option.value}
                  onClick={() =>
                    setFormData({ ...formData, type: option.value })
                  }
                  className={`border rounded p-2 text-sm text-center ${
                    formData.type === option.value
                      ? "bg-[#8B1538] text-white"
                      : "bg-white text-gray-700"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Urgence en radio horizontale */}
          <div>
            <label className="block text-sm font-medium mb-2">Urgence</label>
            <div className="flex gap-4">
              {["Normal", "Urgent"].map((level) => (
                <label key={level} className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="urgence"
                    value={level}
                    checked={formData.urgence === level}
                    onChange={(e) =>
                      setFormData({ ...formData, urgence: e.target.value })
                    }
                    className="form-radio"
                  />
                  <span className="text-sm">{level}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Détails */}
          <div>
            <label className="block text-sm font-medium">Détails</label>
            <textarea
              className="w-full border p-2 rounded"
              rows="4"
              value={formData.details}
              onChange={(e) =>
                setFormData({ ...formData, details: e.target.value })
              }
            />
          </div>

          <button className="px-4 py-2 bg-green-600 text-white rounded">
            {loadingForm ? "Envoi..." : "Envoyer la demande"}
          </button>
        </form>
      )}

      {/* Chargement */}
      {loading && <p>Chargement des demandes ...</p>}

      {/* Onglets */}
      {!loading && (
        <>
          <div className="flex gap-6 border-b">
            <button
              onClick={() => setActiveTab("all")}
              className={`pb-2 ${
                activeTab === "all"
                  ? "border-b-2 border-blue-600 font-semibold"
                  : "text-gray-500"
              }`}
            >
              Toutes les demandes ({demandes.length})
            </button>

            <button
              onClick={() => setActiveTab("en_cours")}
              className={`pb-2 ${
                activeTab === "en_cours"
                  ? "border-b-2 border-blue-600 font-semibold"
                  : "text-gray-500"
              }`}
            >
              En cours ({enCours.length})
            </button>

            <button
              onClick={() => setActiveTab("terminees")}
              className={`pb-2 ${
                activeTab === "terminees"
                  ? "border-b-2 border-blue-600 font-semibold"
                  : "text-gray-500"
              }`}
            >
              Terminées ({terminees.length})
            </button>
          </div>

          {/* Contenu */}
          <Section
            demandes={getDemandesByTab()}
            renderTypeLabel={renderTypeLabel}
          />
        </>
      )}
    </div>
  );
}

// 🧱 Liste des demandes
function Section({ demandes, renderTypeLabel }) {
  if (demandes.length === 0) {
    return <p className="text-sm text-gray-500">Aucune demande</p>;
  }

  return (
    <ul className="space-y-3">
      {demandes.map((d) => (
        <li key={d.id} className="border p-4 rounded">
          <p className="text-sm font-medium text-slate-800">
            {renderTypeLabel(d.type)}
          </p>
          <p className="text-sm">Urgence : {d.urgence}</p>
          <p className="text-sm text-gray-600">{d.details}</p>
        </li>
      ))}
    </ul>
  );
}

export default Demande;
